import { useRef, useState, type KeyboardEvent } from 'react';
import { palette } from '../../theme';
import { usePortalPalette } from '../../theme/usePortalPalette';
import { Box, CircularProgress, IconButton, Stack, TextField } from '../../primitives';
import * as Icons from '../../icons';
import { iconSize } from '@munifocus/tokens';
import { MentionTextField, type MentionSuggestion } from '../mentions/MentionTextField';

/** A file the user has picked but not yet sent. */
export interface PendingAttachment {
  /** Local id, so a row can be removed before the upload finishes. */
  id: string;
  fileName: string;
  /** True while the bytes are still going up. */
  uploading: boolean;
  /** Set when the upload failed; the row stays so the user can remove it. */
  error?: string;
}

export interface MessageComposerProps {
  /**
   * Send the message. Return the send's promise to have the composer put the
   * text back if it rejects — a failed send must not eat what the user wrote.
   */
  onSend: (body: string) => void | Promise<unknown>;
  /** Omit to hide the attach control entirely (a portal with no upload route). */
  onAttach?: (files: File[]) => void;
  /**
   * `accept` for the file picker. Advisory only — the dialog still lets a
   * determined user pick anything, and both the caller and the server check —
   * but it keeps the common case from offering files that will be refused.
   */
  acceptFileTypes?: string;
  onRemoveAttachment?: (id: string) => void;
  attachments?: PendingAttachment[];
  /** Fired as the user types and when they stop, for the typing indicator. */
  onTypingChange?: (typing: boolean) => void;
  disabled?: boolean;
  /** Set when the thread cannot accept messages — archived, or the user left. */
  disabledReason?: string;
  /**
   * Offer colleagues to @mention while typing. The caller supplies who may be
   * mentioned and keeps track of who was picked; the composer only edits the
   * text. Omit for threads where mentions mean nothing.
   */
  mentions?: {
    suggestions: readonly MentionSuggestion[];
    onPick: (suggestion: MentionSuggestion) => void;
    onQueryChange?: (query: string | null) => void;
    listLabel: string;
  };
  labels: {
    placeholder: string;
    send: string;
    attach: string;
    removeAttachment: string;
    uploading: string;
  };
}

/** Idle time after the last keystroke before the typing signal is withdrawn. */
const TYPING_IDLE_MS = 2_000;

/**
 * The message input.
 *
 * **Enter sends, Shift+Enter breaks a line.** Officers write short operational
 * notes far more often than paragraphs, and requiring a mouse trip to the send
 * button for each one is the wrong default. The multiline case is still one
 * modifier away.
 *
 * **The typing signal is debounced on both edges**: raised on the first
 * keystroke, withdrawn after a pause. Emitting per keystroke would put a socket
 * message behind every character, and never withdrawing it would leave the other
 * side showing "typing…" indefinitely whenever someone abandoned a draft.
 */
export function MessageComposer({
  onSend,
  onAttach,
  acceptFileTypes,
  onRemoveAttachment,
  attachments = [],
  onTypingChange,
  disabled,
  disabledReason,
  mentions,
  labels,
}: MessageComposerProps) {
  const portal = usePortalPalette();
  const [value, setValue] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);
  const typingRef = useRef(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function stopTyping() {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (typingRef.current) {
      typingRef.current = false;
      onTypingChange?.(false);
    }
  }

  function handleChange(next: string) {
    setValue(next);
    if (!onTypingChange) return;

    if (!typingRef.current && next) {
      typingRef.current = true;
      onTypingChange(true);
    }
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(stopTyping, TYPING_IDLE_MS);
  }

  function send() {
    const body = value.trim();
    // An attachment-only message is legitimate — a photo needs no caption — but
    // nothing may be sent while bytes are still moving, or the message would
    // reference a key that does not exist yet.
    if (!canSend(value, attachments, disabled)) return;
    const sending = onSend(body);
    setValue('');
    // Restore the draft on failure, unless they have already started another.
    if (sending instanceof Promise) {
      sending.catch(() => setValue((current) => current || body));
    }
    // Immediately, not on the idle timer: they have stopped composing by
    // definition, and the other side should not still see "typing…" beside a
    // message that has already arrived.
    stopTyping();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    // Never intercept the Enter that commits an IME composition — doing so
    // sends a half-typed word in any language that composes, which is most of
    // them outside Latin scripts.
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    send();
  }

  return (
    <Box
      sx={{
        borderTop: `1px solid ${palette.border.line}`,
        p: 1.5,
        backgroundColor: palette.background.paper,
      }}
    >
      {attachments.length > 0 && (
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {attachments.map((attachment) => (
            <Stack
              key={attachment.id}
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                border: `1px solid ${attachment.error ? palette.semantic.emergency : palette.border.line}`,
                backgroundColor: palette.background.parch,
                maxWidth: '100%',
              }}
            >
              {attachment.uploading ? (
                <CircularProgress size={12} aria-label={labels.uploading} />
              ) : (
                <Icons.Paperclip size={iconSize.xs} aria-hidden />
              )}
              <Box
                component="span"
                sx={{
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: attachment.error ? palette.semantic.emergency : palette.text.primary,
                }}
              >
                {attachment.error ?? attachment.fileName}
              </Box>
              {onRemoveAttachment && (
                <IconButton
                  size="small"
                  onClick={() => onRemoveAttachment(attachment.id)}
                  aria-label={`${labels.removeAttachment}: ${attachment.fileName}`}
                  sx={{ p: 0.25 }}
                >
                  <Icons.X size={iconSize.xs} aria-hidden />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-end' }}>
        {mentions ? (
          <MentionTextField
            value={value}
            onChange={handleChange}
            suggestions={mentions.suggestions}
            onPick={mentions.onPick}
            onQueryChange={mentions.onQueryChange}
            listLabel={mentions.listLabel}
            disabled={disabled}
            maxRows={6}
            placeholder={disabled ? (disabledReason ?? labels.placeholder) : labels.placeholder}
            ariaLabel={labels.placeholder}
            onKeyDown={handleKeyDown}
            onBlur={stopTyping}
            fieldSx={{
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiOutlinedInput-root': { px: 0.5 },
            }}
          />
        ) : (
          <TextField
            fullWidth
            multiline
            maxRows={6}
            size="small"
            value={value}
            disabled={disabled}
            placeholder={disabled ? (disabledReason ?? labels.placeholder) : labels.placeholder}
            onChange={(event) => handleChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={stopTyping}
            aria-label={labels.placeholder}
            sx={{
              // No outline around the field. The composer already sits in its own
              // bar, separated from the transcript by a hairline, so the input's
              // own border only draws a second box inside the first.
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiOutlinedInput-root': { px: 0.5 },
            }}
          />
        )}
        {onAttach && (
          <>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              accept={acceptFileTypes}
              onChange={(event) => {
                const picked = Array.from(event.target.files ?? []);
                if (picked.length > 0) onAttach(picked);
                // Cleared so picking the same file twice in a row still fires a
                // change event.
                event.target.value = '';
              }}
            />
            <IconButton
              onClick={() => fileInput.current?.click()}
              disabled={disabled}
              aria-label={labels.attach}
              sx={{ color: palette.text.muted }}
            >
              <Icons.Paperclip size={iconSize.lg} aria-hidden />
            </IconButton>
          </>
        )}
        <IconButton
          onClick={send}
          disabled={!canSend(value, attachments, disabled)}
          aria-label={labels.send}
          sx={{
            color: portal.brand.accent,
            // Matches the design system's press feedback rather than a bounce.
            transition: 'transform 200ms',
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          <Icons.Send size={iconSize.lg} aria-hidden />
        </IconButton>
      </Stack>
    </Box>
  );
}

/**
 * Whether there is anything to send.
 *
 * A *failed* upload is not an attachment. Counting one would enable the button
 * on a message with no text and no usable file — the caller filters the missing
 * descriptor out, and the API rejects the result as empty, so the officer sees
 * a send that silently does nothing. The failed row stays on screen either way,
 * so they can retry or remove it.
 */
function canSend(
  value: string,
  attachments: PendingAttachment[],
  disabled: boolean | undefined,
): boolean {
  if (disabled) return false;
  // Nothing may go while bytes are still moving: the message would quote a key
  // that does not exist yet.
  if (attachments.some((a) => a.uploading)) return false;

  const usable = attachments.filter((a) => !a.error && !a.uploading);
  return Boolean(value.trim()) || usable.length > 0;
}
